import React, { useState, useRef, useEffect } from 'react';
import { X, Check, ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import popupImage from '../assets/images/popupTuVan.jpg';
import { cmsApi } from '../services/cmsApi';
import { useAppConfig } from '../contexts/AppConfigContext';
import { showToast } from '../utils/toast';

interface ConsultationPopupProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void; // Callback khi submit thành công
  source?: 'header' | 'calculator' | 'ebook' | 'promotion'; // Nguồn mở popup
  prizeWon?: string; // Phần thưởng từ vòng quay may mắn
}

const ConsultationPopup: React.FC<ConsultationPopupProps> = ({ isOpen, onClose, onSuccess, source = 'header', prizeWon }) => {
  const { t } = useTranslation();
  const { config } = useAppConfig();
  const consultationServices = config?.consultationServices ?? [];
  const [formData, setFormData] = useState({
    fullName: '',
    phoneNumber: '',
    shopIssue: '',
    revenueScale: '',
    shopLink: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({
    fullName: '',
    phoneNumber: '',
    shopLink: '',
    shopIssue: '',
    revenueScale: ''
  });
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error khi user nhập/chọn
    if (name in errors) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validatePhoneNumber = (phone: string): boolean => {
    const cleanPhone = phone.replace(/\s+/g, '').replace(/[-().]/g, '');
    const phoneRegex = /^(0[3-9]\d{8}|02\d{8,9})$/;
    return phoneRegex.test(cleanPhone);
  };

  const validateShopeeUrl = (url: string): boolean => {
    // Chỉ kiểm tra không được để trống
    return url.trim().length > 0;
  };

  const validateForm = (): boolean => {
    const newErrors = {
      fullName: '',
      phoneNumber: '',
      shopLink: '',
      shopIssue: '',
      revenueScale: ''
    };

    if (!formData.fullName.trim()) {
      newErrors.fullName = t('popup.validation.nameRequired');
    }

    if (!formData.phoneNumber.trim()) {
      newErrors.phoneNumber = t('popup.validation.phoneRequired');
    } else if (!validatePhoneNumber(formData.phoneNumber)) {
      newErrors.phoneNumber = t('popup.validation.phoneInvalid');
    }

    if (!formData.shopIssue) {
      newErrors.shopIssue = 'Vui lòng chọn dịch vụ quan tâm';
    }

    if (!formData.revenueScale) {
      newErrors.revenueScale = 'Vui lòng chọn quy mô doanh thu';
    }

    if (!formData.shopLink.trim()) {
      newErrors.shopLink = t('popup.validation.shopLinkRequired');
    } else if (!validateShopeeUrl(formData.shopLink)) {
      newErrors.shopLink = t('popup.validation.shopLinkInvalid');
    }

    setErrors(newErrors);
    return !newErrors.fullName && !newErrors.phoneNumber && !newErrors.shopLink && !newErrors.shopIssue && !newErrors.revenueScale;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Chuyển đổi revenueScale value sang text hiển thị
      const getRevenueText = (value: string): string => {
        switch (value) {
          case 'under-50m':
            return t('popup.revenueOptions.under50m');
          case '50m-200m':
            return t('popup.revenueOptions.from50to200m');
          case '200m-500m':
            return t('popup.revenueOptions.from200to500m');
          case 'over-500m':
            return t('popup.revenueOptions.over500m');
          default:
            return value;
        }
      };

      const result = await cmsApi.submitConsultation({
        fullName: formData.fullName,
        phoneNumber: formData.phoneNumber,
        shopIssue: formData.shopIssue,
        revenueScale: formData.revenueScale ? getRevenueText(formData.revenueScale) : '',
        shopLink: formData.shopLink,
        prizeWon: prizeWon, // Phần thưởng từ vòng quay (nếu có)
        source, // Nguồn mở popup: header | calculator | ebook | promotion
      });

      if (result.success) {
        // Reset form
        setFormData({
          fullName: '',
          phoneNumber: '',
          shopIssue: '',
          revenueScale: '',
          shopLink: ''
        });

        // Gọi callback onSuccess nếu được cung cấp
        if (onSuccess) {
          onSuccess();
        }

        onClose();

        // Tạm comment: Chuyển hướng đến trang cảm ơn
        // window.location.href = 'https://hcm.tukigroup.vn/cam-on';

        // Hiển thị thông báo thành công và cho phép xem kết quả tính toán
        showToast('Cảm ơn bạn đã đăng ký! Bạn có thể xem kết quả tính toán bên dưới.', 'success');
      } else {
        showToast(result.message || 'Có lỗi xảy ra khi gửi thông tin. Vui lòng thử lại.', 'error');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      showToast('Không thể gửi thông tin. Vui lòng kiểm tra kết nối mạng và thử lại.', 'error');
    }

    setIsSubmitting(false);
  };

  if (!isOpen) return null;
  return (
    <div className="popup-overlay" onClick={onClose}>
      <div className="popup-container" onClick={(e) => e.stopPropagation()}>
        <div className="popup-left">
          <div className="popup-image-container">
            <img src={config?.consultationImage || popupImage} alt="Tư vấn kinh doanh" className="popup-main-image" />
          </div>
        </div>
        <div className="popup-right">
          <button className="popup-close-btn" onClick={onClose}>
            <X size={20} />
          </button>
          <div className="popup-form-container">
            <div className="popup-form-head">
              <h3 className="popup-form-title">
                {source === 'ebook' ? t('popup.popupTitleEbook') : source === 'calculator' ? t('popup.popupTitleCalculator') : t('popup.popupTitle')}
              </h3>
              {source === 'ebook' && (
                <p className="popup-form-subtitle">{t('popup.popupSubtitleEbook')}</p>
              )}
            </div>

            <form onSubmit={handleSubmit} className="popup-form">
              <div className="form-row">
                <div className="form-group">
                  <input
                    type="text"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    placeholder={`${t('popup.namePlaceholder')} *`}
                    className={`form-input ${errors.fullName ? 'error' : ''}`}
                  />
                  {errors.fullName && (
                    <div className="form-error">
                      {errors.fullName}
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <input
                    type="tel"
                    name="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={handleInputChange}
                    placeholder={`${t('popup.phonePlaceholder')} *`}
                    className={`form-input ${errors.phoneNumber ? 'error' : ''}`}
                  />
                  {errors.phoneNumber && (
                    <div className="form-error">
                      {errors.phoneNumber}
                    </div>
                  )}
                </div>
              </div>

              <div className="form-group">
                <div className="custom-dropdown" ref={dropdownRef}>
                  <div
                    className={`custom-dropdown-trigger form-input ${errors.shopIssue ? 'error' : ''}`}
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  >
                    <span className={formData.shopIssue ? 'selected-text' : 'placeholder-text'}>
                      {formData.shopIssue || `${t('popup.shopIssuePlaceholder')} *`}
                    </span>
                    <ChevronDown size={20} className={`dropdown-arrow ${isDropdownOpen ? 'open' : ''}`} />
                  </div>

                  {isDropdownOpen && (
                    <div className="custom-dropdown-menu">
                      {consultationServices.map((svc) => (
                        <div
                          key={svc}
                          className={`custom-dropdown-item ${formData.shopIssue === svc ? 'selected' : ''}`}
                          onClick={() => {
                            setFormData(prev => ({ ...prev, shopIssue: svc }));
                            setErrors(prev => ({ ...prev, shopIssue: '' }));
                            setIsDropdownOpen(false);
                          }}
                        >
                          <div className="check-icon-container">
                            {formData.shopIssue === svc && <Check size={18} className="check-icon" />}
                          </div>
                          <span>{svc}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {errors.shopIssue && (
                  <div className="form-error">
                    {errors.shopIssue}
                  </div>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">{t('popup.revenueScaleLabel')} *</label>
                <div className={`radio-group-horizontal ${errors.revenueScale ? 'error-group' : ''}`}>
                  <label className="radio-option">
                    <input
                      type="radio"
                      name="revenueScale"
                      value="under-50m"
                      checked={formData.revenueScale === 'under-50m'}
                      onChange={handleInputChange}
                    />
                    <span>{t('popup.revenueOptions.under50m')}</span>
                  </label>
                  <label className="radio-option">
                    <input
                      type="radio"
                      name="revenueScale"
                      value="50m-200m"
                      checked={formData.revenueScale === '50m-200m'}
                      onChange={handleInputChange}
                    />
                    <span>{t('popup.revenueOptions.from50to200m')}</span>
                  </label>
                  <label className="radio-option">
                    <input
                      type="radio"
                      name="revenueScale"
                      value="200m-500m"
                      checked={formData.revenueScale === '200m-500m'}
                      onChange={handleInputChange}
                    />
                    <span>{t('popup.revenueOptions.from200to500m')}</span>
                  </label>
                  <label className="radio-option">
                    <input
                      type="radio"
                      name="revenueScale"
                      value="over-500m"
                      checked={formData.revenueScale === 'over-500m'}
                      onChange={handleInputChange}
                    />
                    <span>{t('popup.revenueOptions.over500m')}</span>
                  </label>
                </div>
                {errors.revenueScale && (
                  <div className="form-error">
                    {errors.revenueScale}
                  </div>
                )}
              </div>

              <div className="form-group">
                <input
                  type="text"
                  name="shopLink"
                  value={formData.shopLink}
                  onChange={handleInputChange}
                  placeholder={`${t('popup.shopLinkPlaceholder')} *`}
                  className={`form-input ${errors.shopLink ? 'error' : ''}`}
                />
                {errors.shopLink && (
                  <div className="form-error">
                    {errors.shopLink}
                  </div>
                )}
              </div>

              <button
                type="submit"
                className={`popup-submit-btn ${isSubmitting ? 'submitting' : ''}`}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <div className="loading-spinner"></div>
                    {t('popup.processingText')}
                  </>
                ) : (
                  source === 'ebook' ? t('popup.submitButtonEbook') : source === 'calculator' ? t('popup.submitButtonCalculator') : t('popup.submitButton')
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConsultationPopup; 